import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { Form } from '../../core/models/form.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { BarcodeMappingService } from '../../core/services/barcode-mapping.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { FormService } from '../../core/services/form.service';
import { LoggerService } from '../../core/services/logger.service';
import { NfcAvailability, NfcFailureReason, NfcService, NfcTagContent } from '../../core/services/nfc.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

export type TagTargetStatus = 'pending' | 'written' | 'already' | 'skipped' | 'failed';

/** One tag to write: the checkpoint itself, or one instance of it. */
export interface TagTarget {
  /** '' for a checkpoint without instances */
  instanceId: string;
  label: string;
  status: TagTargetStatus;
  code: string | null;
  /** i18n key explaining a failed/skipped status */
  messageKey: string | null;
}

/** What the page is waiting for while a tag operation runs. */
export type WritePhase = 'idle' | 'write' | 'confirm' | 'overwrite' | 'verify';

export interface OverwritePrompt {
  label: string;
  existing: NfcTagContent;
  /** What the tag currently points to, when it holds a known CheckPoint code */
  currentTarget: string | null;
}

/** Failures that end a bulk run; anything else is per-tag and the run moves on. */
const STOP_REASONS = new Set<NfcFailureReason>([
  'cancelled', 'timeout', 'unsupported', 'no_nfc', 'disabled', 'permission_denied', 'session_error', 'busy',
]);

const FAILURE_KEYS: Record<NfcFailureReason | 'mismatch', string> = {
  unsupported: 'scan.nfc.error.unsupported',
  no_nfc: 'scan.nfc.error.noNfc',
  disabled: 'scan.nfc.error.disabled',
  permission_denied: 'scan.nfc.error.permissionDenied',
  busy: 'scan.nfc.error.busy',
  cancelled: 'nfcTags.status.cancelled',
  timeout: 'scan.nfc.error.timeout',
  tag_lost: 'scan.nfc.error.tagLost',
  unreadable: 'scan.nfc.error.unreadable',
  session_error: 'scan.nfc.error.sessionError',
  read_only: 'nfcTags.error.readOnly',
  too_small: 'nfcTags.error.tooSmall',
  different_tag: 'nfcTags.error.differentTag',
  write_failed: 'nfcTags.error.writeFailed',
  mismatch: 'nfcTags.error.verifyMismatch',
};

/**
 * Writes NFC tags for a checkpoint + form (one per instance for instanced checkpoints).
 * Each tag gets the target's short code — the same code a printed barcode label for that
 * target carries (barcode_mappings, created locally and synced in the background).
 *
 * Policy: blank tags are written; a tag already holding the code is left as is; other
 * content is only replaced after the user confirms (then the same tag is tapped again).
 * Tags are never locked. Optional verification re-reads the tag after writing.
 */
@Component({
  selector: 'app-write-nfc-tags',
  imports: [
    CardComponent,
    CardContentComponent,
    ButtonComponent,
    IconComponent,
    SpinnerComponent,
    TranslatePipe,
    ReactiveFormsModule,
    FormlyModule,
  ],
  templateUrl: './write-nfc-tags.component.html',
  styleUrl: './write-nfc-tags.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WriteNfcTagsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private checkPointService = inject(CheckPointService);
  private formService = inject(FormService);
  private assetInstanceService = inject(AssetInstanceService);
  private barcodeMappingService = inject(BarcodeMappingService);
  private nfc = inject(NfcService);
  private translation = inject(TranslationService);
  private logger = inject(LoggerService).getLogger('write-nfc-tags');

  loading = signal(true);
  loadError = signal<string | null>(null);
  checkPoint = signal<CheckPoint | null>(null);
  form = signal<Form | null>(null);
  targets = signal<TagTarget[]>([]);
  hasInstances = signal(false);

  private availability = signal<NfcAvailability | null>(null);
  nfcReady = computed(() => this.availability()?.available === true);
  nfcDisabled = computed(() => {
    const a = this.availability();
    return a?.available === false && a.reason === 'disabled';
  });

  phase = signal<WritePhase>('idle');
  activeLabel = signal<string | null>(null);
  overwritePrompt = signal<OverwritePrompt | null>(null);
  bulkRunning = signal(false);
  busy = computed(() => this.phase() !== 'idle');

  completedCount = computed(() => this.targets().filter((t) => t.status === 'written' || t.status === 'already').length);

  optionsForm = new FormGroup({});
  optionsModel = { verify: true };
  optionsFields: FormlyFieldConfig[] = [];

  private overwriteDecision: ((overwrite: boolean) => void) | null = null;
  private stopRequested = false;
  /** The tag written last — ignored by the next session while it still lies on the phone */
  private lastWritten: { uid: string | null; code: string } | null = null;
  private checkPointId = '';
  private formId = '';

  async ngOnInit() {
    this.optionsFields = [
      {
        key: 'verify',
        type: 'checkbox',
        props: { label: this.translation.translate('nfcTags.options.verify') },
      },
    ];
    this.availability.set(await this.nfc.getAvailability());
    await this.loadData();
  }

  ngOnDestroy() {
    this.stopRequested = true;
    this.decideOverwrite(false);
    this.nfc.cancel();
  }

  async loadData() {
    this.loading.set(true);
    try {
      this.checkPointId = this.route.snapshot.paramMap.get('checkpointId') ?? '';
      this.formId = this.route.snapshot.paramMap.get('formId') ?? '';
      const [checkPoint, form, instances] = await Promise.all([
        this.checkPointService.getById(this.checkPointId),
        this.formService.getById(this.formId),
        this.assetInstanceService.getByCheckpointId(this.checkPointId),
      ]);
      if (!checkPoint || !form) {
        this.loadError.set(this.translation.translate('nfcTags.error.notFound'));
        return;
      }
      this.checkPoint.set(checkPoint);
      this.form.set(form);

      const active = instances.filter((i) => !i.is_deleted);
      this.hasInstances.set(active.length > 0);
      const targets = active.length
        ? active.map((i) => this.newTarget(i.id, this.translation.translate('nfcTags.instanceLabel', { serial: i.serial_number })))
        : [this.newTarget('', checkPoint.name)];
      // Show codes that already exist (e.g. from printed barcode labels)
      for (const target of targets) {
        const mapping = await this.barcodeMappingService.getByTarget(this.checkPointId, this.formId, target.instanceId);
        target.code = mapping?.barcode_value ?? null;
      }
      this.targets.set(targets);
    } catch (err) {
      this.logger.error('Failed to load NFC tag data', err);
      this.loadError.set(this.translation.translate('nfcTags.error.loadFailed'));
    } finally {
      this.loading.set(false);
    }
  }

  async writeOne(target: TagTarget) {
    if (this.busy()) return;
    this.stopRequested = false;
    await this.processTarget(target);
  }

  /** Writes every target not yet done, one tag after another. */
  async writeAll() {
    if (this.busy()) return;
    this.stopRequested = false;
    this.bulkRunning.set(true);
    try {
      for (const target of this.targets()) {
        if (target.status === 'written' || target.status === 'already') continue;
        const next = await this.processTarget(target);
        if (next === 'stop' || this.stopRequested) break;
      }
    } finally {
      this.bulkRunning.set(false);
    }
  }

  cancel() {
    this.stopRequested = true;
    this.decideOverwrite(false);
    this.nfc.cancel();
  }

  confirmOverwrite() {
    this.decideOverwrite(true);
  }

  skipOverwrite() {
    this.decideOverwrite(false);
  }

  openSettings() {
    this.nfc.openSettings();
  }

  async refreshAvailability() {
    this.availability.set(await this.nfc.getAvailability());
  }

  done() {
    this.router.navigate(['/checkpoints', this.checkPointId, this.formId, 'view']);
  }

  private newTarget(instanceId: string, label: string): TagTarget {
    return { instanceId, label, status: 'pending', code: null, messageKey: null };
  }

  private async processTarget(target: TagTarget): Promise<'continue' | 'stop'> {
    this.activeLabel.set(target.label);
    try {
      // Optimistic local write; syncs in the background like any other row
      const mapping = await this.barcodeMappingService.getOrCreateForTarget(this.checkPointId, this.formId, target.instanceId);
      const code = mapping.barcode_value;
      this.update(target, { code });
      const previous = this.lastWritten;
      const ignoreTag = (tag: NfcTagContent) =>
        !!previous && previous.code !== code && ((!!tag.uid && tag.uid === previous.uid) || tag.code === previous.code);
      const alertMessage = this.translation.translate('nfcTags.iosAlert.write', { label: target.label });

      this.phase.set('write');
      let result = await this.nfc.writeCode(code, { alertMessage, ignoreTag });

      if (!result.success && result.reason === 'has_other_data') {
        const existing = result.existing;
        this.phase.set('confirm');
        const overwrite = await this.askOverwrite(target, existing);
        if (!overwrite) {
          this.update(target, { status: 'skipped', messageKey: this.stopRequested ? 'nfcTags.status.cancelled' : 'nfcTags.status.skippedOther' });
          return this.stopRequested ? 'stop' : 'continue';
        }
        this.phase.set('overwrite');
        result = await this.nfc.writeCode(code, {
          alertMessage: this.translation.translate('nfcTags.iosAlert.overwrite', { label: target.label }),
          overwrite: true,
          expectedUid: existing.uid,
        });
      }

      if (!result.success) {
        return this.fail(target, result.reason as NfcFailureReason);
      }

      if (result.outcome === 'already_written') {
        this.update(target, { status: 'already', messageKey: null });
        this.lastWritten = { uid: result.uid, code };
        return 'continue';
      }

      if (this.optionsModel.verify) {
        this.phase.set('verify');
        const verified = await this.nfc.verifyCode(code, {
          alertMessage: this.translation.translate('nfcTags.iosAlert.verify', { label: target.label }),
          expectedUid: result.uid,
        });
        if (!verified.success) return this.fail(target, verified.reason);
      }

      this.update(target, { status: 'written', messageKey: null });
      this.lastWritten = { uid: result.uid, code };
      return 'continue';
    } catch (err) {
      this.logger.error('NFC tag write failed', err);
      this.update(target, { status: 'failed', messageKey: 'nfcTags.error.writeFailed' });
      return 'stop';
    } finally {
      this.phase.set('idle');
      this.activeLabel.set(null);
      this.overwritePrompt.set(null);
    }
  }

  private fail(target: TagTarget, reason: NfcFailureReason | 'mismatch'): 'continue' | 'stop' {
    this.logger.info('NFC tag not written', { label: target.label, reason });
    if (reason === 'disabled' || reason === 'no_nfc') this.refreshAvailability();
    const stop = reason !== 'mismatch' && STOP_REASONS.has(reason);
    if (reason === 'cancelled') {
      this.update(target, { status: 'pending', messageKey: null });
    } else {
      this.update(target, { status: 'failed', messageKey: FAILURE_KEYS[reason] });
    }
    return stop ? 'stop' : 'continue';
  }

  private async askOverwrite(target: TagTarget, existing: NfcTagContent): Promise<boolean> {
    let currentTarget: string | null = null;
    if (existing.code) {
      const mapping = await this.barcodeMappingService.getByBarcodeValue(existing.code);
      if (mapping) {
        const sameCheckPoint = mapping.asset_id === this.checkPointId;
        const instance = mapping.asset_instance_id ? this.targets().find((t) => t.instanceId === mapping.asset_instance_id) : null;
        if (sameCheckPoint && instance) {
          currentTarget = instance.label;
        } else {
          const other = sameCheckPoint ? this.checkPoint() : await this.checkPointService.getById(mapping.asset_id);
          currentTarget = other?.name ?? null;
        }
      }
    }
    this.overwritePrompt.set({ label: target.label, existing, currentTarget });
    if (this.stopRequested) return false;
    return new Promise<boolean>((resolve) => (this.overwriteDecision = resolve));
  }

  private decideOverwrite(overwrite: boolean) {
    const decide = this.overwriteDecision;
    this.overwriteDecision = null;
    this.overwritePrompt.set(null);
    decide?.(overwrite);
  }

  private update(target: TagTarget, changes: Partial<TagTarget>) {
    Object.assign(target, changes);
    this.targets.set([...this.targets()]);
  }
}
